import { useCallback, useEffect, useRef, useState } from 'react';

export interface Async<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
  /** True while a refresh is in flight over data we already have. Used to avoid
   *  blanking the screen on a poll — an operations display that flickers to
   *  "loading" every thirty seconds is unreadable. */
  refreshing: boolean;
}

/**
 * Run an async function, keep the last good result while reloading, and
 * optionally poll.
 *
 * There is no cache and no shared store. Every screen asks the API directly; the
 * fleet is eight units and the answers are small.
 */
export function useAsync<T>(
  fn: () => Promise<T>,
  deps: unknown[],
  options: { pollMs?: number } = {},
): Async<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tick, setTick] = useState(0);
  const generation = useRef(0);
  const hasData = useRef(false);

  const run = useCallback(async () => {
    const mine = ++generation.current;
    if (hasData.current) setRefreshing(true);
    else setLoading(true);
    try {
      const result = await fn();
      if (generation.current !== mine) return; // a newer request won
      setData(result);
      hasData.current = true;
      setError(null);
    } catch (err) {
      if (generation.current !== mine) return;
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (generation.current === mine) {
        setLoading(false);
        setRefreshing(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    hasData.current = false;
    void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run, tick]);

  useEffect(() => {
    if (!options.pollMs) return;
    const id = setInterval(() => void run(), options.pollMs);
    return () => clearInterval(id);
  }, [run, options.pollMs]);

  return { data, error, loading, refreshing, reload: () => setTick((t) => t + 1) };
}
